/**
 * 纯逻辑层：与渲染无关的派生规则。
 *
 * 单独抽出来有两个原因：
 *  1. 便于单元测试（不依赖 JSX / DOM）
 *  2. 这两处是复刻中最容易读错的规则，值得独立成文
 */
import type { AnswerKey, FlowStep, Guide, Quest, Selection } from '../types';

/* -------------------------------------------------------------------------- */
/* 一、结果盘面的 7 行                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 结果行 = story.questions 中 affect 为 true 的题目，按原顺序取作答键的大写。
 *
 * 原站：`questions.filter(q => q.affect).map(q => selection[q.key].toUpperCase())`
 * 本作 13 题里正好 7 题 affect 为真 —— 这就是"7 行"以及进度条分母 7 的来历
 * （线上 14.2857% = 1/7）。
 *
 * 与原站的差异：原站若某道 affect 题没有作答记录会直接抛
 * `undefined.toUpperCase()`（只靠 Sentry 错误边界兜底），这里回退为 'F'。
 */
export function deriveAnswers(questions: Quest[], selection: Selection): string[] {
  return questions
    .filter((q) => q.affect)
    .map((q) => (selection[q.key] ?? 'f').toUpperCase());
}

/** 同意 / 不同意的占比（progress bar 的 flexBasis 百分比） */
export function deriveDistribution(list: string[]): { agree: number; disagree: number; agreePct: number; disagreePct: number } {
  const total = list.length || 1;
  const agree = list.filter((v) => v === 'T').length;
  const disagree = list.filter((v) => v === 'F').length;
  return {
    agree,
    disagree,
    agreePct: (100 * agree) / total,
    disagreePct: (100 * disagree) / total,
  };
}

/** 排名展示：原站 rank.toFixed(0).padStart(7,'0') */
export function formatRank(rank: number | null | undefined): string {
  if (rank === null || rank === undefined || !rank) return '';
  return rank.toFixed(0).padStart(7, '0');
}

/* -------------------------------------------------------------------------- */
/* 二、流程推进                                                               */
/* -------------------------------------------------------------------------- */

export type CurrentStep =
  | { kind: 'quest'; quest: Quest; qId: string }
  | { kind: 'guide'; guide: Guide; gId: string }
  | null;

/**
 * 逐字还原原站 Et 组件的推进算法：
 *   遍历 flow
 *     quest 未作答          -> 立即返回该题（结束遍历）
 *     quest 已作答          -> 跳过，并清空已缓冲的 guide
 *     缓冲非空时遇到未答题  -> 本轮返回 null（让位，避免引导与题目同时出现）
 *     guide 已访问          -> 跳过
 *     guide 未访问          -> 收入缓冲区
 *   遍历结束：缓冲区非空 -> 返回第一个 guide；否则 null
 *
 * 这里只需要 key，不依赖题目的 affect 等展示字段，因此用最小结构约束。
 */
export interface QuestLike {
  key: string;
}

export function selectCurrent(
  flow: FlowStep[],
  story: { guides: Record<string, Guide>; questions: QuestLike[] },
  selection: Selection,
  visitedGuides: string[],
): CurrentStep {
  const buffered: FlowStep[] = [];

  for (const step of flow) {
    if (step.type === 'quest') {
      const quest = story.questions.find((q) => q.key === step.key);
      if (!quest) continue;
      if (selection[quest.key]) {
        buffered.length = 0;
        continue;
      }
      if (buffered.length > 0) return null;
      // 调用方的题目对象可能带额外字段（content/affect/options），这里按最小结构消费
      return { kind: 'quest', quest: quest as Quest, qId: quest.key };
    }

    const guide = story.guides[step.key];
    if (!guide) continue;
    if (visitedGuides.includes(step.key)) continue;
    buffered.push(step);
  }

  const first = buffered[0];
  if (!first) return null;
  const guide = story.guides[first.key];
  return guide ? { kind: 'guide', guide, gId: first.key } : null;
}

/* -------------------------------------------------------------------------- */
/* 三、引导文案                                                               */
/* -------------------------------------------------------------------------- */

/**
 * 引导文本的 {nickname} 替换。
 * 原站：nickName 存在则取 '#' 前一段，否则用"博士"。
 * 注意 nickName 为 "null#7393" 时替换结果是字面量 "null" —— 原站即如此，不做特殊处理。
 *
 * 注意：默认值写在函数体内而不是参数默认值，因为 Node 的类型剥离（node --test 直接跑 .ts）
 * 会丢掉参数默认值，写在体内才能让测试与浏览器行为一致。
 */
export const DEFAULT_NICKNAME = '博士';

export function fillNickname(text: string, nickName?: string | null, fallback?: string): string {
  const fb = fallback || DEFAULT_NICKNAME;
  const name = nickName ? nickName.split('#')[0] : fb;
  return text.replace(/\{nickname\}/g, name);
}

/** 取引导当前页的文本与选项（type 1 / 2 / 其他） */
export function resolveGuidePage(
  guide: Guide,
  page: number,
  selection: Selection,
  nickName: string | null | undefined,
): { text: string; options: string[]; hasDueAnswer: boolean } {
  const fill = (t: string) => fillNickname(t, nickName);

  if (guide.type === 1 && Array.isArray(guide.content)) {
    const rows = (guide.options ?? []) as Array<string | string[]>;
    const row = rows[page];
    return {
      text: fill(guide.content[page] ?? ''),
      options: Array.isArray(row) ? row : row ? [row] : [],
      hasDueAnswer: true,
    };
  }

  if (guide.type === 2 && guide.contents) {
    const due = guide.due ? selection[guide.due] : undefined;
    if (!due) {
      // 原站在此渲染空文本 + 空按钮，形成无法推进的死胡同；
      // 这里如实标注，由调用方降级为"点击任意处继续"。
      return { text: '', options: [], hasDueAnswer: false };
    }
    return {
      text: fill(guide.contents[due as AnswerKey] ?? ''),
      options: (guide.options ?? []) as string[],
      hasDueAnswer: true,
    };
  }

  return { text: fill((guide.content as string) ?? ''), options: (guide.options ?? []) as string[], hasDueAnswer: true };
}
